import { Conditions, conditionsIsEmpty, getConditionIds } from "utils/insight/conditions";
import { IndexProvider } from "utils/insight/summary";
export class ModelFeatureSetFactory {
    metadata;
    constructor(metadata) {
        this.metadata = metadata;
    }
    getModelTrainingJobFeatureSet(job) {
        return Object.entries(job?.models ?? {}).reduce((obj, [modelName, rawModel]) => {
            obj[modelName] = this.getModelFeatureSet(rawModel);
            return obj;
        }, {});
    }
    getModelFeatureSet(raw) {
        const sets = [];
        const conditionSetFactory = this.metadata.getConditionSetFactory();
        Object.entries(raw?.features?.percentages ?? {}).forEach(([column, feature]) => {
            const id = conditionSetFactory.getId(column);
            if (id !== undefined) {
                const numLevels = this.metadata.getColumn(id).getLevelCount();
                const indexProvider = new IndexProvider([id], [numLevels]);
                const rawFeatures = Object.entries(feature.values)
                    .map(([level, value]) => ({
                    conditions: conditionSetFactory.getConditionSet({ [column]: level }),
                    value
                }))
                    .filter(({ conditions }) => !conditionsIsEmpty(conditions));
                if (rawFeatures.length > 0) {
                    const featuresByConditions = rawFeatures.reduce((obj, x) => {
                        obj[x.conditions.toString()] = x;
                        return obj;
                    }, {});
                    const features = [];
                    let last = 0;
                    for (const i of indexProvider.getIndices(Conditions.emptySet())) {
                        const c = indexProvider.getConditions(i);
                        const f = featuresByConditions[c.toString()];
                        if (f === undefined) {
                            features.push({
                                conditions: c,
                                value: last
                            });
                        }
                        else {
                            last = f.value;
                            features.push(f);
                        }
                    }
                    sets.push(new ModelFeatureSubSet(features, indexProvider));
                }
            }
        });
        return ModelFeatureComposite.create(sets);
    }
}
export class ModelFeatureSet {
}
class ModelFeatureSubSet extends ModelFeatureSet {
    features;
    indexProvider;
    constructor(features, indexProvider) {
        super();
        this.features = features;
        this.indexProvider = indexProvider;
    }
    size() {
        return this.features.length;
    }
    [Symbol.iterator]() {
        return this.features[Symbol.iterator]();
    }
    getIds() {
        return this.indexProvider.getIds();
    }
    getFeature(conditions) {
        return this.features[this.indexProvider.getIndex(conditions)];
    }
    getFeatures(conditions = Conditions.emptySet()) {
        if (conditionsIsEmpty(conditions)) {
            return this.features;
        }
        return this.indexProvider
            .getIndices(conditions)
            .filter((i) => i >= 0)
            .map((i) => this.features[i])
            .filter((summary) => summary !== undefined);
    }
    subset(ids) {
        const thisIds = this.indexProvider.ids;
        if (thisIds.every(ids.includes) && ids.every(thisIds.includes)) {
            return this;
        }
        return EmptyModelFeatureSet.INSTANCE;
    }
}
class ModelFeatureComposite extends ModelFeatureSet {
    features;
    constructor(features) {
        super();
        this.features = features;
    }
    getFeature(conditions) {
        return this.subset(getConditionIds(conditions)).getFeature(conditions);
    }
    getFeatures(conditions = Conditions.emptySet()) {
        return Object.values(this.features).flatMap((subset) => subset.getFeatures(conditions));
    }
    [Symbol.iterator]() {
        throw this.getFeatures()[Symbol.iterator]();
    }
    subset(ids) {
        const key = ModelFeatureComposite._encodeIds(ids);
        return this.features[key] ?? EmptyModelFeatureSet.INSTANCE;
    }
    static _encodeIds(ids) {
        return JSON.stringify(ids.slice().sort());
    }
    static create(sets) {
        const features = sets.reduce((obj, next) => {
            obj[ModelFeatureComposite._encodeIds(next.getIds())] = next;
            return obj;
        }, {});
        return new ModelFeatureComposite(features);
    }
}
class EmptyModelFeatureSet extends ModelFeatureSet {
    static INSTANCE = new EmptyModelFeatureSet();
    constructor() {
        super();
    }
    subset() {
        return this;
    }
    getFeature() {
        return undefined;
    }
    getFeatures() {
        return [];
    }
    [Symbol.iterator]() {
        return [][Symbol.iterator]();
    }
}
